import { createElement } from 'react';
import {
  RiArrowLeftRightLine,
  RiBookOpenLine,
  RiBuilding2Line,
  RiCalendarLine,
  RiCompass3Line,
  RiContrastDropLine,
  RiDashboard2Line,
  RiDashboard3Line,
  RiDashboardHorizontalLine,
  RiDashboardLine,
  RiDatabase2Line,
  RiDropLine,
  RiFileGifLine,
  RiFileLine,
  RiFileTextLine,
  RiFocus3Line,
  RiFunctions,
  RiHeading,
  RiHome2Line,
  RiImageLine,
  RiInformationLine,
  RiKeyboardLine,
  RiLayoutMasonryLine,
  RiLineChartLine,
  RiMapPinLine,
  RiPaintBrushLine,
  RiPlayLine,
  RiPlugLine,
  RiPriceTag3Line,
  RiPulseLine,
  RiRfidFill,
  RiRulerLine,
  RiScales3Line,
  RiSettings4Line,
  RiShapesLine,
  RiTeamLine,
  RiTempHotLine,
  RiTimeLine,
  RiUserSettingsLine,
  RiWaterFlashLine,
  type RemixiconComponentType,
} from '@remixicon/react';
import { cn } from '@/shared/lib/cn';
import { ICON_REGISTRY } from '@/shared/ui/icon';

/** Icons referenced by name from serialisable data (persisted tabs, section icons): the shell's glyphs + section presets. */
const BUNDLED: Record<string, RemixiconComponentType> = {
  'arrow-left-right-line': RiArrowLeftRightLine,
  'book-open-line': RiBookOpenLine,
  'building-2-line': RiBuilding2Line,
  'calendar-line': RiCalendarLine,
  'compass-3-line': RiCompass3Line,
  'contrast-drop-line': RiContrastDropLine,
  'dashboard-2-line': RiDashboard2Line,
  'dashboard-3-line': RiDashboard3Line,
  'dashboard-horizontal-line': RiDashboardHorizontalLine,
  'dashboard-line': RiDashboardLine,
  'database-2-line': RiDatabase2Line,
  'drop-line': RiDropLine,
  'file-gif-line': RiFileGifLine,
  'file-line': RiFileLine,
  'file-text-line': RiFileTextLine,
  'focus-3-line': RiFocus3Line,
  functions: RiFunctions,
  heading: RiHeading,
  'home-2-line': RiHome2Line,
  'image-line': RiImageLine,
  'information-line': RiInformationLine,
  'keyboard-line': RiKeyboardLine,
  'layout-masonry-line': RiLayoutMasonryLine,
  'line-chart-line': RiLineChartLine,
  'map-pin-line': RiMapPinLine,
  'paint-brush-line': RiPaintBrushLine,
  'play-line': RiPlayLine,
  'plug-line': RiPlugLine,
  'price-tag-3-line': RiPriceTag3Line,
  'pulse-line': RiPulseLine,
  'rfid-fill': RiRfidFill,
  'ruler-line': RiRulerLine,
  'scales-3-line': RiScales3Line,
  'settings-4-line': RiSettings4Line,
  'team-line': RiTeamLine,
  'temp-hot-line': RiTempHotLine,
  'time-line': RiTimeLine,
  'user-settings-line': RiUserSettingsLine,
  'water-flash-line': RiWaterFlashLine,
};

const KIT = ICON_REGISTRY as Record<string, RemixiconComponentType>;

/**
 * Name → component: the shell's set, then the UI kit's registry (`shared/ui/icon`), else a neutral glyph.
 * Static maps only — importing the whole Remix set (~2.4 MB) would defeat tree-shaking for every icon import.
 * Own-property checks: names come from data and must never resolve to `Object.prototype` members.
 */
export const resolveIcon = (name: string): RemixiconComponentType =>
  (Object.hasOwn(BUNDLED, name) ? BUNDLED[name] : Object.hasOwn(KIT, name) ? KIT[name] : undefined) ?? RiShapesLine;

/** A Remix icon addressed by its kebab-case name (`"drop-line"`), e.g. a persisted tab or a section icon. */
export function NamedIcon({ name, className }: { name: string; className?: string }) {
  // a lookup in static maps, not a component created during render
  return createElement(resolveIcon(name), { 'aria-hidden': true, className });
}

/** Solid coloured square with a white glyph (01-design-system §1.7). */
export function IconChip({
  name,
  icon: Icon,
  color,
  colorClass,
  size,
  className,
}: {
  name?: string;
  icon?: RemixiconComponentType;
  /** data colour (section colour, user-picked) */
  color?: string;
  /** palette class when the colour is part of the design (`bg-dark-blue-1000`) */
  colorClass?: string;
  size: 20 | 24;
  className?: string;
}) {
  const glyph = size === 20 ? 'size-10' : 'size-16';
  return (
    <span
      aria-hidden
      className={cn('flex shrink-0 items-center justify-center text-white', size === 20 ? 'size-20 rounded-6' : 'size-24 rounded-6', colorClass, className)}
      style={color ? { backgroundColor: color } : undefined}
    >
      {Icon ? <Icon className={glyph} /> : name ? <NamedIcon name={name} className={glyph} /> : null}
    </span>
  );
}
